/**
 * ActiveSpeakerContext — single TTS instance for the entire app.
 *
 * Enforces the exclusivity rule: only one persona speaks at a time.
 * If persona A is active and persona B calls speak(), the call is silently dropped.
 * Same persona calls are queued sequentially.
 *
 * Story v19-1-2: TTS Coordinator — Fix Dual Voice Bug
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTTS } from '../hooks/useTTS';

export interface ActiveSpeakerState {
  activePersonaSlug: string | null;
  isPlaying: boolean;
  isQueuePlaying: boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  speak: (text: string, personaSlug: string, messageId?: number, voiceId?: string) => void;
  stop: () => void;
}

export const ActiveSpeakerContext = createContext<ActiveSpeakerState | null>(null);

/** Convenience hook — also available from `hooks/useActiveSpeaker.ts`. */
export function useActiveSpeaker(): ActiveSpeakerState {
  const ctx = useContext(ActiveSpeakerContext);
  if (!ctx) {
    throw new Error('useActiveSpeaker must be used within an ActiveSpeakerProvider');
  }
  return ctx;
}

export function ActiveSpeakerProvider({ children }: { children: React.ReactNode }) {
  const tts = useTTS();
  const [activePersonaSlug, setActivePersonaSlug] = useState<string | null>(null);

  // Reset activePersonaSlug when all playback finishes
  useEffect(() => {
    if (!tts.isQueuePlaying && !tts.isPlaying) {
      setActivePersonaSlug(null);
    }
  }, [tts.isQueuePlaying, tts.isPlaying]);

  const speak = useCallback(
    (text: string, personaSlug: string, messageId?: number, voiceId?: string) => {
      // Exclusivity rule: drop calls from a different active persona
      if (activePersonaSlug !== null && activePersonaSlug !== personaSlug) {
        return;
      }
      setActivePersonaSlug(personaSlug);
      tts.queueSentence(text, messageId ?? 0, voiceId);
    },
    [activePersonaSlug, tts],
  );

  const stop = useCallback(() => {
    tts.clearQueue();
    setActivePersonaSlug(null);
  }, [tts]);

  return (
    <ActiveSpeakerContext.Provider
      value={{
        activePersonaSlug,
        isPlaying: tts.isPlaying,
        isQueuePlaying: tts.isQueuePlaying,
        audioRef: tts.audioRef,
        speak,
        stop,
      }}
    >
      {children}
    </ActiveSpeakerContext.Provider>
  );
}
